import { useState, useEffect } from "react";
import { useAdmin } from "../context/AdminContext";

export interface EntitySelectorProps {
  entityType: 'participant' | 'group' | 'chat-message' | 'thread' | 'workspace' | 'file' | 'tag';
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  label?: string;
  placeholder?: string;
  customOptions?: EntityOption[]; // Optional pre-loaded options
}

interface EntityOption {
  id: string;
  label: string;
  metadata?: string;
}

const ENDPOINT_MAP: Record<string, string> = {
  participant: 'participants',
  group: 'groups',
  message: 'messages',
  thread: 'threads',
  workspace: 'workspaces',
  file: 'files',
  tag: 'tags',
};

export function EntitySelector({
  entityType,
  value,
  onChange,
  required = false,
  label,
  placeholder = 'Select...',
  customOptions,
}: EntitySelectorProps) {
  const { adminApp } = useAdmin();
  const [options, setOptions] = useState<EntityOption[]>([]);
  const [loading, setLoading] = useState(false);

  // Use custom options if provided, otherwise load from API
  useEffect(() => {
    if (customOptions) {
      setOptions(customOptions);
    } else {
      loadOptions();
    }
  }, [entityType, customOptions]);

  const loadOptions = async () => {
    setLoading(true);
    try {
      const endpoint = ENDPOINT_MAP[entityType];
      const data = await adminApp.fetchEntities<Record<string, unknown>>(endpoint);
      
      // Transform data to options based on entity type
      const transformed = data.map((item) => {
        let itemLabel = '';
        let metadata = '';

        switch (entityType) {
          case 'tag':
            itemLabel = `${item.name} (${item.type})`;
            metadata = item.namespace as string;
            break;
          case 'participant':
            itemLabel = `${item.name} - ${item.type}`;
            metadata = item.status as string;
            break;
          case 'group':
            itemLabel = item.name as string;
            metadata = `${item.participantCount || 0} members`;
            break;
          case 'chat-message':
            itemLabel = `${(item.content as string)?.slice(0, 50)}...`;
            metadata = `${item.role} - ${new Date(item.timestamp as string).toLocaleString()}`;
            break;
          case 'thread':
            itemLabel = item.title as string || item.id as string;
            metadata = new Date(item.createdAt as string).toLocaleDateString();
            break;
          case 'workspace':
            itemLabel = item.name as string || item.id as string;
            break;
          case 'file':
            itemLabel = item.name as string || item.filename as string;
            metadata = item.type as string;
            break;
          default:
            itemLabel = item.id as string;
        }

        return {
          id: item.id as string,
          label: itemLabel,
          metadata,
        };
      });

      setOptions(transformed);
    } catch (error) {
      console.error(`Failed to load ${entityType} options:`, error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        disabled={loading}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:opacity-50"
      >
        <option value="">{loading ? 'Loading...' : placeholder}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
            {option.metadata && ` [${option.metadata}]`}
          </option>
        ))}
      </select>

      {!loading && options.length === 0 && (
        <p className="mt-1 text-xs text-yellow-600 dark:text-yellow-400">
          No {entityType}s found. Create one first.
        </p>
      )}
    </div>
  );
}

